import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, ElementRef, inject, input, output, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { map } from 'rxjs';

import { PrivaFormComponent } from '@priva/components/form';
import { PrivaFormGroupComponent } from '@priva/components/form-group';
import { PrivaFormInputGroupComponent } from '@priva/components/form-input-group';
import { PrivaInputComponent } from '@priva/components/input';
import { PrivaPopoverComponent } from '@priva/components/popover';
import { PrivaLocalizationModule } from '@priva/localization';

import { Activity } from '../../features/activity-selector/models';
import {
    convertFormValueToNumberOrUndefined,
    isNumberWithPointSeparator,
    validateAmountResultForActivity,
} from '../../utils';
import { AmountResultFormValue } from './amount-result-form-value';
import { AmountResultStore } from './amount-result.store';

@Component({
    selector: 'app-amount-result',
    templateUrl: './amount-result.component.html',
    styleUrl: './amount-result.component.scss',
    imports: [
        AsyncPipe,
        FormsModule,
        ReactiveFormsModule,
        PrivaFormGroupComponent,
        PrivaFormInputGroupComponent,
        PrivaFormComponent,
        PrivaInputComponent,
        PrivaLocalizationModule,
        PrivaPopoverComponent,
    ],
    providers: [AmountResultStore],
})
export class AmountResultComponent {
    private componentStore = inject(AmountResultStore);
    private destroyRef = inject(DestroyRef);

    activityAndAmountResult = input(undefined, {
        transform: (
            value: {
                activity: Activity | undefined;
                amountResult: number | undefined;
            } | null,
        ) => {
            this.componentStore.updateActivityAndAmountResult({
                activity: value?.activity,
                amountResult: value?.amountResult,
            });
        },
    });

    amountResultUpdated = output<number | undefined>();

    readonly amountInput = viewChild('amountInput', { read: ElementRef });

    readonly amountResultControl = new FormControl<string>('');
    readonly formGroup = new FormGroup(
        {
            activity: new FormControl<Activity | undefined>(undefined),
            amountResult: this.amountResultControl,
        },
        (formGroup) => {
            const { activity, amountResult }: AmountResultFormValue = formGroup.value;
            return validateAmountResultForActivity({
                amountString: amountResult ?? '',
                activity: activity ?? undefined,
            });
        },
    );

    readonly unit$ = this.componentStore.activity$.pipe(
        map((activity) => activity?.measurementUnit || activity?.registrationUnit),
    );

    constructor() {
        this.componentStore.formValues$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ activity, amountResult }) => {
                this.formGroup.setValue({ activity, amountResult });
            });

        this.amountResultControl.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe((amountResultFormValue) => {
                if (isNumberWithPointSeparator(this.amountResultControl.value ?? '')) {
                    const amountResult = convertFormValueToNumberOrUndefined(amountResultFormValue);

                    this.amountResultUpdated.emit(amountResult);
                }
            });
    }

    blurInput() {
        return this.amountInput()?.nativeElement?.blur();
    }
}
